import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import { alpha } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import RtlButton from './RtlButton';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNotification } from './CustomNotification';
import { useDialogs } from '../hooks/useDialogs/useDialogs';
import {
  updateInstallment,
  createInstallment,
  deleteInstallment,
  type Installment,
  type UpdateInstallmentPayload,
  type CreateInstallmentPayload,
  type PaymentMethodType,
} from '../data/orders';
import dayjs from 'dayjs';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface InstallmentEditDialogProps {
  open: boolean;
  installment: Installment | null;
  orderId: number;
  isEdit?: boolean;
  totalAmount?: number;
  allInstallments?: Installment[];
  onClose: () => void;
  onSuccess?: () => void;
  onDelete?: () => void;
}

export default function InstallmentEditDialog({
  open,
  installment,
  orderId,
  isEdit = false,
  totalAmount,
  allInstallments = [],
  onClose,
  onSuccess,
  onDelete,
}: InstallmentEditDialogProps) {
  const { t, direction } = useLanguage();
  const notifications = useNotification();
  const dialogs = useDialogs();
  const isRtl = direction === 'rtl';
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [dueDate, setDueDate] = React.useState('');
  const [amount, setAmount] = React.useState<number | ''>('');
  const [paymentMethod, setPaymentMethod] = React.useState<PaymentMethodType>('CASH');
  const [errors, setErrors] = React.useState<{ dueDate?: string; amount?: string }>({});

  const installmentsSum = allInstallments.reduce(
    (sum, inst) => sum + (typeof inst.amount === 'string' ? parseFloat(inst.amount) : inst.amount),
    0,
  );
  const isAmountEditable =
    !isEdit || totalAmount === undefined || Math.abs(installmentsSum - totalAmount) >= 0.001;

  // Budget available for this installment = totalAmount minus all other installments' amounts
  const otherInstallmentsSum = React.useMemo(() => {
    if (totalAmount === undefined) return Infinity;
    return allInstallments.reduce((sum, inst) => {
      if (inst.id === installment?.id) return sum;
      return sum + (typeof inst.amount === 'string' ? parseFloat(inst.amount) : inst.amount);
    }, 0);
  }, [allInstallments, installment?.id, totalAmount]);

  const maxAllowedAmount = totalAmount !== undefined ? totalAmount - otherInstallmentsSum : Infinity;

  const currentAmount = typeof amount === 'number' ? amount : 0;
  const isSaveDisabled =
    isSubmitting ||
    (totalAmount !== undefined && currentAmount > maxAllowedAmount + 0.001);

  React.useEffect(() => {
    if (open) {
      if (isEdit && installment) {
        setDueDate(dayjs(installment.due_date).format('YYYY-MM-DD'));
        setAmount(
          typeof installment.amount === 'string'
            ? parseFloat(installment.amount)
            : installment.amount,
        );
        setPaymentMethod(installment.payment_method || 'CASH');
      } else {
        // New installment
        setDueDate('');
        setAmount('');
        setPaymentMethod('CASH');
      }
      setErrors({});
    }
  }, [open, installment, isEdit]);

  const handleDueDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDueDate(event.target.value);
    if (errors.dueDate) {
      setErrors({ ...errors, dueDate: undefined });
    }
  };

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value === '') {
      setAmount('');
      if (errors.amount) setErrors({ ...errors, amount: undefined });
      return;
    }
    const parsed = parseFloat(value);
    if (!isNaN(parsed) && parsed >= 0) {
      const capped = maxAllowedAmount !== Infinity ? Math.min(parsed, maxAllowedAmount) : parsed;
      setAmount(capped);
      if (errors.amount) setErrors({ ...errors, amount: undefined });
    }
  };

  const handlePaymentMethodChange = (event: SelectChangeEvent) => {
    setPaymentMethod(event.target.value as PaymentMethodType);
  };

  const validate = (): boolean => {
    const newErrors: { dueDate?: string; amount?: string } = {};
    if (!dueDate) {
      newErrors.dueDate = t('validation.required');
    }
    if (amount === '' || amount === null || amount === undefined || amount < 0) {
      newErrors.amount = t('validation.required');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    setIsSubmitting(true);
    try {
      if (isEdit && installment?.id) {
        const payload: UpdateInstallmentPayload = {};
        if (dueDate) payload.due_date = dueDate;
        if (amount !== '' && amount !== null && amount !== undefined) payload.amount = Number(amount);
        if (paymentMethod) payload.payment_method = paymentMethod;

        await updateInstallment(installment.id, payload);

        notifications.show(t('messages.updateSuccess'), {
          severity: 'success',
          autoHideDuration: 3000,
        });
      } else {
        const payload: CreateInstallmentPayload = {
          due_date: dueDate,
          amount: Number(amount),
          payment_method: paymentMethod,
        };

        await createInstallment(orderId, payload);

        notifications.show(t('messages.createSuccess'), {
          severity: 'success',
          autoHideDuration: 3000,
        });
      }

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      notifications.show(`${t('messages.errorOccurred')}: ${(error as Error).message}`, {
        severity: 'error',
        autoHideDuration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!installment?.id) return;

    const confirmed = await dialogs.confirm(t('messages.deleteConfirm'), {
      title: t('orders.deleteInstallment'),
      severity: 'error',
      okText: t('common.delete'),
      cancelText: t('common.cancel'),
    });

    if (!confirmed) return;

    setIsSubmitting(true);
    try {
      await deleteInstallment(installment.id);

      notifications.show(t('messages.deleteSuccess'), {
        severity: 'success',
        autoHideDuration: 3000,
      });

      if (onDelete) {
        onDelete();
      }
      onClose();
    } catch (error) {
      notifications.show(`${t('messages.errorOccurred')}: ${(error as Error).message}`, {
        severity: 'error',
        autoHideDuration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Transition}
      PaperProps={{
        component: 'form',
        onSubmit: (e: React.FormEvent) => {
          e.preventDefault();
          handleSubmit();
        },
        sx: (theme) => ({
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(2, 139, 203, 0.15)',
          background: theme.palette.background.paper,
          ...theme.applyStyles('dark', {
            background: alpha('#1a1a1a', 0.95),
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
          }),
        }),
      }}
    >
      <DialogTitle
        sx={{
          pb: 2,
          pt: 3,
          px: 3,
          background: (theme) =>
            `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          fontWeight: 600,
          fontSize: { xs: '1.25rem', sm: '1.5rem' },
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        {isEdit ? <EditIcon sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }} /> : <AddIcon sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }} />}
        {isEdit ? t('orders.editInstallment') : t('orders.addInstallment')}
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 2, sm: 3 }, pt: 4, overflow: 'visible' }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }} sx={{ pt: 1 }}>
            <TextField
              type="date"
              label={t('orders.dueDate')}
              value={dueDate}
              onChange={handleDueDateChange}
              fullWidth
              required
              error={!!errors.dueDate}
              helperText={errors.dueDate || ' '}
              InputLabelProps={{
                shrink: true,
                sx: {
                  textAlign: isRtl ? 'right' : 'left',
                  right: isRtl ? 0 : 'auto',
                  left: isRtl ? 'auto' : 0,
                  transformOrigin: isRtl ? 'top right' : 'top left',
                },
              }}
              FormHelperTextProps={{
                sx: {
                  textAlign: isRtl ? 'right' : 'left',
                  marginRight: isRtl ? '14px' : 0,
                  marginLeft: isRtl ? 0 : '14px',
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              type="number"
              label={t('orders.amount')}
              value={amount}
              onChange={handleAmountChange}
              fullWidth
              required
              disabled={!isAmountEditable}
              inputProps={{ min: 0, step: 0.01, max: maxAllowedAmount !== Infinity ? maxAllowedAmount : undefined }}
              error={!!errors.amount}
              helperText={!isAmountEditable ? t('orders.amountLockedUntilSumMatchesTotal') : (errors.amount || ' ')}
              InputLabelProps={{
                sx: {
                  textAlign: isRtl ? 'right' : 'left',
                  right: isRtl ? 0 : 'auto',
                  left: isRtl ? 'auto' : 0,
                  transformOrigin: isRtl ? 'top right' : 'top left',
                },
              }}
              FormHelperTextProps={{
                sx: {
                  textAlign: isRtl ? 'right' : 'left',
                  marginRight: isRtl ? '14px' : 0,
                  marginLeft: isRtl ? 0 : '14px',
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormControl fullWidth>
              <InputLabel
                sx={{
                  textAlign: isRtl ? 'right' : 'left',
                  right: isRtl ? 0 : 'auto',
                  left: isRtl ? 'auto' : 0,
                  transformOrigin: isRtl ? 'top right' : 'top left',
                }}
              >
                {t('orders.paymentMethod')}
              </InputLabel>
              <Select
                value={paymentMethod}
                onChange={handlePaymentMethodChange}
                label={t('orders.paymentMethod')}
                sx={{
                  textAlign: isRtl ? 'right' : 'left',
                }}
              >
                <MenuItem value="CASH">{t('orders.cash')}</MenuItem>
                <MenuItem value="CHEQUE">{t('orders.cheque')}</MenuItem>
                <MenuItem value="BANK_TRANSFER">{t('orders.bankTransfer')}</MenuItem>
                <MenuItem value="CARD">{t('orders.card')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions
        sx={(theme) => ({
          px: 3,
          py: 2.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          background: theme.palette.background.default,
          ...theme.applyStyles('dark', {
            background: alpha('#0a0a0a', 0.8),
            borderColor: alpha('#ffffff', 0.1),
          }),
          justifyContent: isEdit ? 'space-between' : 'flex-end',
        })}
      >
        {isEdit && (
          <Button
            onClick={handleDelete}
            color="error"
            disabled={isSubmitting || installment?.status === 'PAID'}
            sx={(theme) => ({
              borderRadius: 2,
              px: 3,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: theme.palette.error.dark,
              },
            })}
          >
            {t('common.delete')}
          </Button>
        )}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            onClick={onClose}
            disabled={isSubmitting}
            sx={(theme) => ({
              borderRadius: 2,
              px: 3,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' ? alpha('#ffffff', 0.08) : 'grey.100',
              },
            })}
          >
            {t('common.cancel')}
          </Button>
          <RtlButton
            type="submit"
            variant="contained"
            loading={isSubmitting}
            disabled={isSaveDisabled}
            onClick={handleSubmit}
            sx={{
              background: '#028bcb !important',
              boxShadow: 'unset !important',
              border: '0 !important',
              '&:hover': { background: '#0277a8 !important' },
            }}
          >
            {isEdit ? t('common.save') : t('common.create')}
          </RtlButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

